/**
 * Viewport tile transport across the worker boundary (Requirement 1.4, the
 * design's concurrency row: `SharedArrayBuffer` for viewport tiles when COOP/COEP
 * headers are present, transferable `ArrayBuffer` copies otherwise).
 *
 * A `TileSnapshot` is already flat by construction — four typed arrays, an interned
 * string table, no per-cell objects — so the only question is how its buffers reach
 * the main thread. Two answers, chosen by capability:
 *
 * - **Shared.** When the page is cross-origin isolated, buffers are allocated as
 *   `SharedArrayBuffer` and the message carries references. Nothing is copied and
 *   nothing is neutered, so the same tile can be handed to several readers.
 * - **Copy.** Otherwise the tile's own `ArrayBuffer`s are listed as transferables:
 *   ownership moves to the receiver in one move rather than a structured-clone copy
 *   per array. This is sound because `getTile` allocates fresh arrays per call, so
 *   neutering them cannot damage kernel state.
 *
 * The copy path is the default everywhere `crossOriginIsolated` is not true, which
 * includes Node and Vitest, so it is the path the tests exercise. The shared path
 * is selected by {@link preferredTileTransport} and is otherwise identical in
 * shape, which is what keeps the fallback from being a second, less-tested code
 * path with its own bugs.
 *
 * Strings travel as a plain `string[]`. They are interned per tile, so a viewport
 * of repeated categories costs one copy of each distinct value; encoding them into
 * a byte buffer would save nothing a structured clone does not already do well.
 */

import {
  errorFromOrdinal,
  KIND_BOOL,
  KIND_EMPTY,
  KIND_ERROR,
  KIND_NUMBER,
  KIND_TEXT,
} from '../core/store/chunk.js';
import type { TileSnapshot } from '../core/store/sheet.js';
import { EMPTY, type CellValue, type Range } from '../core/types.js';

/** How a tile's buffers cross the boundary. */
export type TileTransportMode = 'shared' | 'copy';

/**
 * A tile in transit. Buffer-typed rather than view-typed so the same message
 * shape carries `ArrayBuffer` and `SharedArrayBuffer` without a second union.
 */
export interface TileMessage {
  transport: TileTransportMode;
  range: Range;
  rows: number;
  cols: number;
  strings: string[];
  kinds: ArrayBufferLike;
  numbers: ArrayBufferLike;
  codes: ArrayBufferLike;
  styleIds: ArrayBufferLike;
}

/** True when this realm can allocate shared memory the other side may read. */
export function sharedMemoryAvailable(): boolean {
  return (
    typeof SharedArrayBuffer !== 'undefined' &&
    (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated === true
  );
}

/**
 * The transport to use here and now.
 *
 * Cross-origin isolation is a property of the page's response headers, not of the
 * build, so it is decided at runtime on every request rather than cached: a
 * development server without COOP/COEP and a production deployment with them run
 * the same bundle.
 */
export function preferredTileTransport(): TileTransportMode {
  return sharedMemoryAvailable() ? 'shared' : 'copy';
}

/** What the sender must hand to `postMessage` as its transfer list. */
export interface EncodedTile {
  message: TileMessage;
  transfer: Transferable[];
}

function sharedU8(source: Uint8Array): SharedArrayBuffer {
  const buffer = new SharedArrayBuffer(source.byteLength);
  new Uint8Array(buffer).set(source);
  return buffer;
}

function sharedF64(source: Float64Array): SharedArrayBuffer {
  const buffer = new SharedArrayBuffer(source.byteLength);
  new Float64Array(buffer).set(source);
  return buffer;
}

function sharedI32(source: Int32Array): SharedArrayBuffer {
  const buffer = new SharedArrayBuffer(source.byteLength);
  new Int32Array(buffer).set(source);
  return buffer;
}

/**
 * Prepares a tile for `postMessage`.
 *
 * `mode` is passed in rather than sniffed so the requester decides: the main
 * thread knows whether *it* can read shared memory, and asking the worker to guess
 * on its behalf is how a mismatch happens.
 */
export function encodeTile(tile: TileSnapshot, mode: TileTransportMode): EncodedTile {
  if (mode === 'shared' && typeof SharedArrayBuffer !== 'undefined') {
    return {
      message: {
        transport: 'shared',
        range: tile.range,
        rows: tile.rows,
        cols: tile.cols,
        strings: tile.strings,
        kinds: sharedU8(tile.kinds),
        numbers: sharedF64(tile.numbers),
        codes: sharedI32(tile.codes),
        styleIds: sharedI32(tile.styleIds),
      },
      // Shared buffers are referenced, never transferred: neutering them would
      // defeat the point of sharing them.
      transfer: [],
    };
  }
  const message: TileMessage = {
    transport: 'copy',
    range: tile.range,
    rows: tile.rows,
    cols: tile.cols,
    strings: tile.strings,
    kinds: tile.kinds.buffer,
    numbers: tile.numbers.buffer,
    codes: tile.codes.buffer,
    styleIds: tile.styleIds.buffer,
  };
  return {
    message,
    // `getTile` allocated these four buffers for this call alone, so moving them
    // costs the kernel nothing.
    transfer: [
      tile.kinds.buffer as ArrayBuffer,
      tile.numbers.buffer as ArrayBuffer,
      tile.codes.buffer as ArrayBuffer,
      tile.styleIds.buffer as ArrayBuffer,
    ],
  };
}

/** Rebuilds views over the received buffers. Allocation-free in both modes. */
export function decodeTile(message: TileMessage): TileSnapshot {
  return {
    range: message.range,
    rows: message.rows,
    cols: message.cols,
    kinds: new Uint8Array(message.kinds),
    numbers: new Float64Array(message.numbers),
    codes: new Int32Array(message.codes),
    strings: message.strings,
    styleIds: new Int32Array(message.styleIds),
  };
}

/**
 * Reads one cell out of a tile as a {@link CellValue}.
 *
 * The Grid_Renderer paints from the flat arrays directly and never calls this; it
 * exists for the formula bar, the status bar, and tests, where one cell at a time
 * is the natural unit. `row` and `col` are absolute sheet coordinates.
 */
export function tileCell(tile: TileSnapshot, row: number, col: number): CellValue {
  const r = tile.range;
  if (row < r.r0 || row > r.r1 || col < r.c0 || col > r.c1) {
    throw new RangeError(`cell (${row}, ${col}) is outside the tile`);
  }
  const at = (row - r.r0) * tile.cols + (col - r.c0);
  switch (tile.kinds[at]) {
    case KIND_EMPTY:
      return EMPTY;
    case KIND_NUMBER:
      return { k: 'number', v: tile.numbers[at] as number };
    case KIND_BOOL:
      return { k: 'bool', v: tile.numbers[at] !== 0 };
    case KIND_TEXT:
      return { k: 'text', v: tile.strings[tile.codes[at] as number] ?? '' };
    case KIND_ERROR:
      return { k: 'error', v: errorFromOrdinal(tile.codes[at] as number) };
    default:
      return EMPTY;
  }
}

/** The style id painted under `(row, col)`. Absolute sheet coordinates. */
export function tileStyleId(tile: TileSnapshot, row: number, col: number): number {
  const r = tile.range;
  if (row < r.r0 || row > r.r1 || col < r.c0 || col > r.c1) {
    throw new RangeError(`cell (${row}, ${col}) is outside the tile`);
  }
  return tile.styleIds[(row - r.r0) * tile.cols + (col - r.c0)] as number;
}
